/**
 * «Сегодня» — дела от всех агентов одной лентой (§5.1 п. 3).
 *
 * Лента — только то, что ждёт человека: накладные, вопросы агента, свои
 * задачи. Каждая строка ведёт туда, где дело делается.
 *
 * С 18.09.2026 над лентой — **главные цифры по правам**, а под ней —
 * **поле «спросить агента»**: §2 ТЗ портала требовал и того, и другого с
 * самого начала, и владелец, посмотрев экран, попросил доделать. Оба
 * блока — свои файлы: у ленты один источник, у цифр четыре, а у вопроса
 * вовсе три разных агента.
 */

import { useQuery } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { Empty, ErrorState, Loading } from '@shell/states';
import { portalApi } from '../api';
import type { Navigation, Today as TodayData, TodayItem, TodayKind } from '../api';
import { Icon } from '../icons';
import { Link } from '../router';
import { tone } from '../ui';
import { Numbers } from './Numbers';

const GROUPS: { title: string; kinds: TodayKind[] }[] = [
  {
    title: 'Накладные',
    kinds: ['receipt_check', 'receipt_ready', 'receipt_waiting', 'receipt_posted', 'duplicate'],
  },
  { title: 'Вопросы агента ко мне', kinds: ['question'] },
  { title: 'Мои задачи', kinds: ['task'] },
];

const ACTION: Record<TodayKind, string> = {
  receipt_check: 'Проверить',
  receipt_ready: 'Провести',
  receipt_waiting: 'Открыть',
  receipt_posted: 'Разобрать',
  duplicate: 'К дублям',
  question: 'Ответить',
  task: 'Открыть',
};

function greeting(name: string | null): string {
  const hour = new Date().getHours();
  const part = hour < 5 ? 'Доброй ночи' : hour < 12 ? 'Доброе утро' : hour < 18 ? 'Добрый день' : 'Добрый вечер';
  const first = name?.split(/\s+/)[0];
  return first ? `${part}, ${first}` : part;
}

function todayLine(): string {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(),
  );
}

export function Today({ nav }: { nav: Navigation }) {
  const hasOps = Boolean(nav.services.ops) || nav.user.is_host;
  const query = useQuery<TodayData, ApiError>({
    queryKey: ['today'],
    queryFn: () => portalApi.today(),
    enabled: hasOps,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return (
    <div className="page page--narrow">
      <header className="page__head">
        <p className="page__eyebrow">{todayLine()}</p>
        <h1 className="page__title">{greeting(nav.user.name)}</h1>
      </header>
      <Numbers nav={nav} />
      {!hasOps || query.error?.isForbidden ? (
        <Empty
          title="Дел на сегодня нет"
          text="Здесь появятся дела от агентов: накладные, вопросы и задачи."
        />
      ) : query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <TodayList items={query.data.items} />
      )}
    </div>
  );
}

function TodayList({ items }: { items: TodayItem[] }) {
  if (items.length === 0) {
    return <Empty title="Всё сделано" text="Новых дел нет. Лента обновляется сама." />;
  }
  return (
    <div className="stack-lg">
      {GROUPS.map((group) => {
        const inGroup = items.filter((item) => group.kinds.includes(item.kind));
        if (inGroup.length === 0) return null;
        return (
          <section key={group.title}>
            <h2 className="group-title">
              {group.title} <span className="group-title__count">{inGroup.length}</span>
            </h2>
            <ul className="task-card">
              {inGroup.map((item, index) => (
                <li key={`${item.kind}-${index}`} className="task">
                  <span className={`task__mark task__mark--${tone(item.colour)}`} aria-hidden="true" />
                  <div className="task__body">
                    {item.link ? (
                      <Link to={item.link} className="task__title">
                        {item.title}
                      </Link>
                    ) : (
                      <span className="task__title">{item.title}</span>
                    )}
                    {item.subtitle && <span className="task__meta">{item.subtitle}</span>}
                  </div>
                  {item.link && (
                    <Link to={item.link} className="ghost-button" tabIndex={-1} aria-hidden="true">
                      {ACTION[item.kind] ?? 'Открыть'}
                      {/^https?:/.test(item.link) && <Icon name="external" size={14} />}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
